import { useEffect, useMemo, useRef, useState } from 'react';
import { presetFor, ROLE_IDS, validateSetup, type RoleId } from '@noxbury/engine';
import { CameraDot, Notice, Panel, Seal } from '../components/Bits';
import { SoundBoardPanel } from '../components/SoundBoardPanel';
import { DashboardGrid } from '../dashboard/DashboardGrid';
import { ambienceFor, CROSSFADE_MS, MEDIA } from '../lib/media';
import { ambience, soundBoard, WIN_CUES, type Cue } from '../lib/sound';
import {
  getCameraBrowserHelp,
  getVideoDevices,
  openMediaMTXCamera,
  hostCameraPath,
  hostCameraUrl,
  type CameraDevice,
} from '../lib/mediamtxCamera';
import { describeEvent, type LogEntry } from '../lib/eventLog';
import { phaseName, roleName, timeOfDay, WIN_NAMES } from '../lib/format';
import type { Connection } from '../lib/socket';
import type { HostState } from '../lib/types';

interface Props {
  connection: Connection<HostState>;
  cameraToken: string;
}


/** Welche Ansage zu welcher Phase gehört. */
const PHASE_CUES: Partial<Record<string, Cue>> = {
  NIGHT_START: 'NIGHT',
  DRUNK_REVEAL: 'DRUNK',
  MORNING_REVEAL: 'MORNING',
  DISCUSSION: 'DAY',
  DAY_VOTE: 'VOTING',
  VOTE_REVEAL: 'VOTE_REVEAL',
  MAYOR_ELECTION: 'MAYOR_ELECTION',
};

export function HostScreen({ connection, cameraToken }: Props) {
  const view = connection.view;
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  /*
   * Klang und Musik laufen hier, nicht im Overlay: So stellt jeder seine Lautstärke selbst ein,
   * und OBS nimmt den Ton dieses Rechners auf, statt eine für alle festgelegte Lautstärke.
   */
  const phase = view?.state.phase;
  const step = view?.night?.step;
  const winners = view?.state.result?.winners;
  useEffect(() => {
    if (!phase) return;

    const cue: Cue | null =
      phase === 'GAME_END'
        ? (WIN_CUES[winners?.[0] ?? ''] ?? 'VICTORY')
        : step
          ? 'ROLE_CALL'
          : (PHASE_CUES[phase] ?? null);

    if (!cue) return;

    // Das Overlay benötigt maximal 1100 ms für die Kamerabewegung.
    // Der Sound startet erst danach mit einem kleinen Sicherheitspuffer.
    const timer = window.setTimeout(() => {
      void soundBoard.play(cue);
    }, 1200);

    // Wird vorher zur nächsten Phase gewechselt, darf der alte Cue
    // nicht verspätet abgespielt werden.
    return () => window.clearTimeout(timer);
  }, [phase, step, winners?.[0]]);

  const mood = view ? ambienceFor(view.state.night ? 'NIGHT' : 'DAY', view.state.phase) : 'START';
  useEffect(() => {
    ambience.crossfadeTo(MEDIA[mood].music, CROSSFADE_MS);
  }, [mood]);
  useEffect(() => () => ambience.stop(), []);

  const run = async (command: unknown, label: string) => {
    const res = await connection.send(command);
    setNote({ ok: res.ok, text: res.ok ? `${label} übernommen.` : (res.message ?? 'Das war nicht möglich.') });
  };

  if (connection.status === 'closed') {
    return (
      <main className="page">
        <Panel title="Lobby geschlossen">
          <p>Die Lobby wurde beendet und kann nicht mehr betreten werden.</p>
        </Panel>
      </main>
    );
  }

  if (connection.status === 'error') {
    return (
      <main className="page">
        <Notice>{connection.error}</Notice>
      </main>
    );
  }
  if (!view) {
    return (
      <main className="page">
        <p className="muted pulse">Regiepult wird geladen …</p>
      </main>
    );
  }

  const state = view.state;
  const alive = view.players.filter((p) => p.status === 'ALIVE').length;

  return (
    <div className="control-room">
      <header className="stage">
        <div className="stage-head">
          <div>
            <p className="tiny muted" style={{ margin: 0 }}>
              Lobby {view.lobby.code} · Partie {state.gameNumber} · Runde {Math.max(1, state.round)} · {alive} am Leben
            </p>
            <h2 style={{ fontSize: '1.5rem' }}>{phaseName(state.phase)}</h2>
          </div>
          <div className="row">
            <button type="button" onClick={() => void run({ type: 'SET_PAUSED', paused: !state.paused }, 'Pause')}>
              {state.paused ? 'Fortsetzen' : 'Anhalten'}
            </button>
            {state.phase === 'ROLE_SETUP' && (
              <button
                type="button"
                onClick={() => void run({ type: 'RETURN_TO_LOBBY' }, 'Zurück zur Lobby')}
              >
                Zurück in die Lobby
              </button>
            )}
            {state.phase !== 'LOBBY' && <AbortButton run={run} />}
            <CloseLobbyButton connection={connection} />
          </div>
        </div>

        {note && (
          <p className={`tiny stage-note${note.ok ? ' muted' : ''}`}>{note.text}</p>
        )}
      </header>

      <DashboardGrid
        modules={[
          {
            id: 'flow',
            title: `Ablauf · ${roundLabel(view)}`,
            content: <FlowModule view={view} run={run} connection={connection} />,
          },
          { id: 'actions', title: 'Entscheidungen', content: <DecisionsPanel view={view} run={run} /> },
          { id: 'win', title: 'Sieg', content: <WinPanel view={view} run={run} /> },
          { id: 'roster', title: 'Mitspieler', content: <RosterPanel view={view} run={run} /> },
          {
            id: 'queue',
            title: 'Letzte Verfügungen',
            content: state.effectQueue.length > 0 ? <QueuePanel view={view} run={run} /> : null,
          },
          { id: 'vote', title: 'Abstimmung', content: view.dayVote ? <VotePanel view={view} run={run} /> : null },
          {
            id: 'election',
            title: 'Bürgermeisterwahl',
            content: view.election ? <ElectionPanel view={view} run={run} /> : null,
          },
          { id: 'log', title: 'Protokoll', content: <LogPanel connection={connection} /> },
          { id: 'twitch', title: 'Twitch-Chats', content: <TwitchPanel connection={connection} view={view} /> },
          { id: 'links', title: 'Links', content: <LinksPanel view={view} /> },
          { id: 'sound', title: 'Klang', content: <SoundBoardPanel /> },
          { id: 'camera', title: 'Deine Kamera', content: <HostCameraPanel connection={connection} view={view} cameraToken={cameraToken} /> },
          { id: 'admin', title: 'Notfallkorrekturen', content: <AdminPanel view={view} run={run} /> },
        ]}
      />
    </div>
  );
}

type Run = (command: unknown, label: string) => Promise<void>;

/** Steht in der Überschrift des Ablaufs: „Nacht 2“ oder „Tag 3“. */
export function roundLabel(view: HostState): string {
  const round = Math.max(1, view.state.round);
  if (view.state.night) return `Nacht ${view.state.night.number}`;
  if (view.state.phase === 'LOBBY' || view.state.phase === 'ROLE_SETUP') return 'Lobby';
  return `Tag ${round}`;
}

/** Das Ablauf-Modul: wo die Runde steht, welche Rolle dran ist und was als Nächstes möglich ist. */
function FlowModule({
  view,
  run,
  connection,
}: {
  view: HostState;
  run: Run;
  connection: Connection<HostState>;
}) {
  const night = view.state.night;
  const current = view.night?.step ?? null;
  const blockedByVampires =
    !!view.night?.readiness && !view.night.readiness.forceAllowed && !view.night.readiness.complete;
  // Ist die aufgerufene Rolle in Wahrheit der Trunkenbold, gehört das hierher – sonst wartet
  // der Spielleiter auf eine Aktion, die gar nicht kommen kann.
  const drunkHere =
    !!view.night?.spotlight.some((s) => s.dormant) && view.night.spotlight.length > 0
      ? view.night.spotlight
          .filter((s) => s.dormant)
          .map((s) => view.players.find((p) => p.id === s.playerId)?.name ?? '—')
      : [];

  return (
    <div className="stack">
      <p style={{ fontFamily: 'var(--serif)', fontSize: '1.3rem', margin: 0 }}>{phaseName(view.state.phase)}</p>

      <FlowControls view={view} run={run} />
      {view.state.phase === 'ROLE_SETUP' && <SetupPanel view={view} run={run} />}
      {view.state.phase === 'LOBBY' && <TestFill connection={connection} view={view} />}
      {view.state.phase === 'MAYOR_ELECTION' && <MayorControls view={view} run={run} />}

      {night && (
        <>
          <div className="role-dial">
            {night.steps.map((step, i) => {
              const aktiv = step === current;
              const nächste = i === night.stepIndex + 1;
              return (
                <button
                  key={step}
                  type="button"
                  className={`role-chip${aktiv ? ' active' : ''}${i < night.stepIndex ? ' done' : ''}`}
                  disabled={aktiv || blockedByVampires}
                  title={nächste ? 'Als nächste Rolle aufrufen' : 'Direkt aufrufen (wird protokolliert)'}
                  onClick={() =>
                    void run(
                      nächste
                        ? { type: 'NEXT_NIGHT_STEP' }
                        : {
                            type: 'ADMIN',
                            change: { kind: 'SET_PHASE', phase: step },
                            reason: 'Rolle direkt aufgerufen',
                          },
                      phaseName(step),
                    )
                  }
                >
                  {phaseName(step)}
                </button>
              );
            })}
          </div>
          <p className="tiny muted" style={{ margin: 0 }}>
            „Nächste Rolle“ folgt der festgelegten Reihenfolge. Du kannst jede Rolle aber auch direkt
            anklicken, etwa um eine vergessene noch einmal aufzurufen.
          </p>
        </>
      )}

      {drunkHere.length > 0 && (
        <Notice calm>
          Hier sitzt der Trunkenbold ({drunkHere.join(', ')}) – vor Nacht 3 kommt keine Aktion.
        </Notice>
      )}
    </div>
  );
}

/** Bürgermeister bestätigen oder im Zweifel jemand anderen einsetzen. */
function MayorControls({ view, run }: { view: HostState; run: Run }) {
  const [pick, setPick] = useState('');
  const leaders = view.election?.leaders ?? [];
  const open = view.state.mayorElection.status === 'OPEN';

  async function appoint(force: boolean) {
    if (!pick) return;
    if (open) await run({ type: 'CLOSE_MAYOR_ELECTION' }, 'Wahl geschlossen');
    await run({ type: 'CONFIRM_MAYOR', playerId: pick, override: force }, 'Bürgermeister');
  }

  return (
    <div className="stack">
      <select value={pick} onChange={(e) => setPick(e.target.value)}>
        <option value="">Bürgermeister wählen …</option>
        {view.players
          .filter((p) => p.status === 'ALIVE')
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.number}. {p.name}
              {leaders.includes(p.id) ? ' (führt)' : ''}
            </option>
          ))}
      </select>
      <div className="row">
        <button type="button" className="primary" disabled={!pick} onClick={() => void appoint(false)}>
          Bestätigen
        </button>
        <button type="button" disabled={!pick} onClick={() => void appoint(true)}>
          Trotzdem einsetzen
        </button>
      </div>
      <p className="tiny muted">
        „Bestätigen“ übernimmt das Ergebnis des Chats. Stimmt niemand ab oder geht etwas schief, setzt
        „Trotzdem einsetzen“ die gewählte Person ein – das wird protokolliert.
      </p>
    </div>
  );
}

/** Abbruch mit Rückfrage: Die Runde endet ohne Sieger, alle kehren in die Lobby zurück. */
function CloseLobbyButton({ connection }: { connection: Connection<HostState> }) {
  const [armed, setArmed] = useState(false);
  const [closing, setClosing] = useState(false);

  if (!armed) {
    return (
      <button type="button" className="danger" onClick={() => setArmed(true)}>
        Lobby schließen
      </button>
    );
  }

  return (
    <span className="row">
      <button
        type="button"
        className="danger"
        disabled={closing}
        onClick={() => {
          setClosing(true);
          void connection.emit('lobby:close').then((res) => {
            if (!res.ok) {
              setClosing(false);
              setArmed(false);
            }
          });
        }}
      >
        {closing ? 'Wird geschlossen …' : 'Wirklich schließen'}
      </button>
      <button
        type="button"
        className="ghost"
        disabled={closing}
        onClick={() => setArmed(false)}
      >
        Zurück
      </button>
    </span>
  );
}

/** Abbruch mit Rückfrage: Die Runde endet ohne Sieger, alle kehren in die Lobby zurück. */
function AbortButton({ run }: { run: Run }) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button type="button" className="ghost" onClick={() => setArmed(true)}>
        Runde abbrechen
      </button>
    );
  }
  return (
    <span className="row">
      <button
        type="button"
        className="danger"
        onClick={() => {
          void run({ type: 'ABORT_ROUND', reason: 'Vom Spielleiter abgebrochen' }, 'Abbruch');
          setArmed(false);
        }}
      >
        Wirklich abbrechen
      </button>
      <button type="button" className="ghost" onClick={() => setArmed(false)}>
        Zurück
      </button>
    </span>
  );
}

/** Die Knöpfe, die im aktuellen Moment sinnvoll sind – alles andere bleibt verborgen. */
function FlowControls({ view, run }: { view: HostState; run: Run }) {
  const s = view.state;
  const queueOpen = s.effectQueue.length > 0;
  const buttons: { label: string; command: unknown; primary?: boolean; disabled?: boolean }[] = [];

  switch (s.phase) {
    case 'LOBBY':
      buttons.push({ label: 'Rollen festlegen', command: { type: 'OPEN_ROLE_SETUP' }, primary: true });
      break;
    case 'ROLE_SETUP':
      if (s.rolesAssigned) {
        buttons.push({ label: 'Bürgermeisterwahl starten', command: { type: 'START_MAYOR_ELECTION' }, primary: true });
        buttons.push({ label: 'Ohne Bürgermeister starten', command: { type: 'SKIP_MAYOR_ELECTION' } });
      }
      break;
    case 'MAYOR_ELECTION':
      if (s.mayorElection.status === 'OPEN') {
        buttons.push({ label: 'Wahl schließen', command: { type: 'CLOSE_MAYOR_ELECTION' }, primary: true });
      }
      buttons.push({ label: 'Wahl überspringen', command: { type: 'SKIP_MAYOR_ELECTION' } });
      break;
    case 'GAME_START':
      buttons.push({ label: 'Erste Nacht beginnen', command: { type: 'START_NIGHT' }, primary: true });
      break;
    case 'NIGHT_START':
      buttons.push({ label: 'Erste Rolle aufrufen', command: { type: 'NEXT_NIGHT_STEP' }, primary: true });
      break;
    case 'DRUNK_REVEAL':
    case 'LOVERS_PHASE':
    case 'VAMPIRE_PHASE':
    case 'SEER_PHASE':
    case 'WITCH_PHASE':
    case 'PRIEST_PHASE':
    case 'DEMON_PHASE':
    case 'GHOST_PHASE': {
      const readiness = view.night?.readiness;
      const offen = readiness?.forceAllowed && !readiness.complete;
      if (view.night?.isLastStep) {
        buttons.push({ label: 'Nacht auswerten', command: { type: 'RESOLVE_NIGHT' }, primary: true });
        if (offen) buttons.push({ label: 'Ohne Aktion auswerten', command: { type: 'RESOLVE_NIGHT', force: true } });
      } else {
        buttons.push({ label: 'Nächste Rolle', command: { type: 'NEXT_NIGHT_STEP' }, primary: true });
        if (offen) buttons.push({ label: 'Ohne Aktion weiter', command: { type: 'NEXT_NIGHT_STEP', force: true } });
      }
      break;
    }
    case 'NIGHT_RESOLUTION':
      buttons.push({ label: 'Morgen aufdecken', command: { type: 'START_MORNING' }, primary: true });
      break;
    case 'MORNING_REVEAL':
      if (queueOpen) {
        buttons.push({ label: 'Letzte Verfügungen', command: { type: 'START_DEATH_RESOLUTION' }, primary: true });
      } else {
        buttons.push({ label: 'Beratung eröffnen', command: { type: 'START_DISCUSSION' }, primary: true });
      }
      break;
    case 'DEATH_RESOLUTION':
      if (!queueOpen) {
        buttons.push(
          s.deathResolutionContext === 'DAY'
            ? { label: 'Nacht beginnen', command: { type: 'START_NIGHT' }, primary: true }
            : { label: 'Beratung eröffnen', command: { type: 'START_DISCUSSION' }, primary: true },
        );
      }
      break;
    case 'DISCUSSION':
      buttons.push({ label: 'Abstimmung starten', command: { type: 'START_DAY_VOTE' }, primary: true });
      break;
    case 'DAY_VOTE':
      buttons.push({ label: 'Stimmen aufdecken', command: { type: 'REVEAL_VOTES' }, primary: true });
      break;
    case 'VOTE_REVEAL':
      buttons.push({ label: 'Urteil vollstrecken', command: { type: 'RESOLVE_DAY_VOTE' }, primary: true });
      break;
    case 'DAY_VOTE_RESOLUTION':
      if (queueOpen) {
        buttons.push({ label: 'Letzte Verfügungen', command: { type: 'START_DEATH_RESOLUTION' }, primary: true });
      } else {
        buttons.push({ label: 'Nächste Nacht', command: { type: 'START_NIGHT' }, primary: true });
      }
      break;
    case 'GAME_END':
      buttons.push({ label: 'Neue Runde', command: { type: 'NEW_ROUND' }, primary: true });
      break;
    default:
      break;
  }

  if (buttons.length === 0) return null;
  return (
    <div className="flow-bar">
      {buttons.map((b) => (
        <button
          key={b.label}
          type="button"
          className={b.primary ? 'primary' : ''}
          disabled={b.disabled}
          onClick={() => void run(b.command, b.label)}
        >
          {b.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Vorübergehende Hilfe zum Ausprobieren: füllt die Lobby mit Testfiguren auf. Diese haben keine
 * Kamera und kein Gerät – ihre Aktionen gibst du über „Für jemanden eingeben“ selbst ein.
 */
function TestFill({ connection, view }: { connection: Connection<HostState>; view: HostState }) {
  const count = view.players.filter((p) => p.status !== 'REMOVED').length;
  const [note, setNote] = useState<string | null>(null);

  async function fill(upTo: number) {
    const res = await connection.emit('test:fill', upTo);
    setNote(res.ok ? `${res.data} Testfiguren ergänzt.` : (res.message ?? 'Das ging nicht.'));
  }

  return (
    <div className="test-bar">
      <span className="tiny muted">Zum Ausprobieren ({count} Personen):</span>
      {[6, 8, 10, 12].map((n) => (
        <button key={n} type="button" className="tiny" disabled={count >= n} onClick={() => void fill(n)}>
          auf {n} auffüllen
        </button>
      ))}
      {note && <span className="tiny">{note}</span>}
    </div>
  );
}


function SetupPanel({ view, run }: { view: HostState; run: Run }) {
  const s = view.state;
  const count = view.players.filter((p) => p.status !== 'REMOVED').length;
  const [roles, setRoles] = useState<RoleId[]>(s.setup?.roles ?? []);
  const [drunk, setDrunk] = useState(s.setup?.drunk ?? { enabled: false, coveredRole: 'RANDOM' as RoleId | 'RANDOM' });
  const [ghost, setGhost] = useState(!!s.setup?.ghost);
  const [lovers, setLovers] = useState(!!s.setup?.lovers);

  // Den gespeicherten Serverstand nur beim Wechsel in eine neue Runde
  // bzw. beim Wechsel des Spiels übernehmen. Lokale +/- Änderungen dürfen
  // nicht durch laufende State-Updates des Servers wieder überschrieben werden.
  useEffect(() => {
    if (s.setup) {
      setRoles(s.setup.roles);
      setDrunk(s.setup.drunk);
      setGhost(!!s.setup.ghost);
      setLovers(!!s.setup.lovers);
    }
  }, [s.gameNumber]);

  const tally = useMemo(() => {
    const map = new Map<RoleId, number>();
    for (const r of roles) map.set(r, (map.get(r) ?? 0) + 1);
    return map;
  }, [roles]);

  // Die Anzeige und Validierung beziehen sich immer auf die aktuell
  // ausgewählte Zusammenstellung – nicht auf den zuletzt gespeicherten Stand.
  const currentIssues = useMemo(
    () => validateSetup({ roles, drunk, ghost, lovers }, count),
    [roles, drunk, ghost, lovers, count],
  );

  const change = (role: RoleId, delta: number) => {
    const next = [...roles];
    if (delta > 0) next.push(role);
    else {
      const idx = next.lastIndexOf(role);
      if (idx >= 0) next.splice(idx, 1);
    }
    setRoles(next);
  };

  return (
    <Panel title="Rollen" aside={<span className="tag">{`${roles.length} von ${count}`}</span>}>
      <div className="role-grid">
        {ROLE_IDS.map((role) => {
          const n = tally.get(role) ?? 0;
          return (
            <div key={role} className={`role-tile${n > 0 ? ' used' : ''}`}>
              <span className="role-tile-name">{roleName(role)}</span>
              <span className="role-tile-controls">
                <button type="button" disabled={n === 0} onClick={() => change(role, -1)} aria-label={`${roleName(role)} weniger`}>
                  −
                </button>
                <span className="role-tile-count">{n}</span>
                <button type="button" onClick={() => change(role, 1)} aria-label={`${roleName(role)} mehr`}>
                  +
                </button>
              </span>
            </div>
          );
        })}
      </div>

      <div className="field setup-block">
        <label className="row" style={{ marginBottom: '0.6rem' }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={!!ghost}
            onChange={(e) => setGhost(e.target.checked)}
          />
          <span>Geist mitspielen lassen (fällt der ersten toten Person zu)</span>
        </label>
        <label className="row" style={{ marginBottom: '0.6rem' }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={lovers}
            onChange={(e) => setLovers(e.target.checked)}
          />
          <span>Geliebte mitspielen lassen</span>
        </label>
        <label className="row" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={drunk.enabled}
            onChange={(e) => setDrunk({ ...drunk, enabled: e.target.checked })}
          />
          <span>Trunkenbold mitspielen lassen</span>
        </label>
        {drunk.enabled && (
          <select
            value={drunk.coveredRole}
            onChange={(e) => setDrunk({ ...drunk, coveredRole: e.target.value as RoleId | 'RANDOM' })}
            style={{ marginTop: '0.4rem' }}
          >
            <option value="RANDOM">Verdeckte Rolle zufällig</option>
            {[...new Set(roles)]
              .filter((r) => r !== 'FOOL')
              .map((r) => (
                <option key={r} value={r}>
                  {roleName(r)} verdecken
                </option>
              ))}
          </select>
        )}
      </div>

      {currentIssues.length > 0 && (
        <Notice>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            {currentIssues.map((i) => (
              <li key={i.code}>{i.message}</li>
            ))}
          </ul>
        </Notice>
      )}

      <div className="setup-actions">
        <button
          type="button"
          onClick={() => {
            const preset = presetFor(count);
            if (!preset) return;
            setRoles(preset.roles);
            setDrunk(preset.drunk);
            void run({ type: 'CONFIGURE_ROLES', setup: { ...preset, ghost, lovers } }, `Vorlage für ${count} Spieler`);
          }}
        >
          Vorschlag für {count} Spieler
        </button>
        <button type="button" onClick={() => void run({ type: 'CONFIGURE_ROLES', setup: { roles, drunk, ghost, lovers } }, 'Rollen')}>
          Zusammenstellung speichern
        </button>
        <button
          type="button"
          className="primary"
          disabled={currentIssues.length > 0}
          onClick={async () => {
            // Erst die Zusammenstellung auf dem Bildschirm übernehmen, dann auslosen –
            // sonst würde eine ältere gespeicherte Fassung verteilt.
            await run({ type: 'CONFIGURE_ROLES', setup: { roles, drunk, ghost, lovers } }, 'Zusammenstellung');
            await run({ type: 'ASSIGN_ROLES' }, 'Zufällige Verteilung');
          }}
        >
          {s.rolesAssigned ? 'Neu auslosen' : 'Rollen zufällig verteilen'}
        </button>
      </div>
      <p className="tiny muted" style={{ marginTop: '0.5rem' }}>
        Beim Auslosen werden auch Trunkenbold und Geliebtenpaar zufällig bestimmt.
      </p>
    </Panel>
  );
}

/**
 * Das Entscheidungsfenster: wer gerade an der Reihe ist, was die Rolle entschieden hat und wie du
 * eingreifen kannst. Es zeigt nur die laufende Phase – das ganze Geschehen steht im Protokoll.
 */
function DecisionsPanel({ view, run }: { view: HostState; run: Run }) {
  const nameOf = (id: string | null | undefined) => view.players.find((p) => p.id === id)?.name ?? '—';
  const night = view.night;
  const readiness = night?.readiness;
  const blocked = readiness && !readiness.forceAllowed && !readiness.complete;
  const round = view.state.round;

  // Nur die Entscheidungen der laufenden Rolle beziehungsweise der Abstimmung.
  const kinds = night?.step ? [NIGHT_ACTION[night.step]].filter(Boolean) : ['DAY_VOTE', 'MAYOR_TIEBREAK'];
  const live = view.state.actions.filter(
    (a) => a.round === round && kinds.includes(a.kind) && (a.status === 'PENDING' || a.status === 'CONFIRMED'),
  );

  const morgen = view.state.lastMorningDeaths;
  const urteil = view.state.dayVote?.resolution;
  const offeneVerfügung = view.state.effectQueue[0];
  const verfügungsText: Record<string, string> = {
    ON_DEATH_KILL: 'reißt jemanden mit ins Jenseits',
    TRANSFER_MAYOR: 'muss einen Nachfolger im Amt bestimmen',
  };

  return (
    <div className="stack">
      {offeneVerfügung && (
        <Notice danger={offeneVerfügung.type === 'ON_DEATH_KILL'}>
          <strong>{nameOf(offeneVerfügung.playerId)}</strong>{' '}
          {verfügungsText[offeneVerfügung.type] ?? offeneVerfügung.type}. Bevor die nächste Nacht
          beginnen kann, muss das entschieden sein – im Fenster „Letzte Verfügungen“.
        </Notice>
      )}

      {view.state.phase === 'MAYOR_TIEBREAK' && (
        <Notice danger>
          <strong>Bürgermeisterentscheidung erforderlich:</strong> Bei der Abstimmung herrscht
          Gleichstand. Der Bürgermeister muss die endgültige Entscheidung treffen.
        </Notice>
      )}

      {view.state.phase === 'MORNING_REVEAL' && morgen && (
        <Notice calm>
          {morgen.length === 0
            ? 'Diese Nacht hat Noxbury niemanden verloren.'
            : `Am Morgen gefunden: ${morgen.map(nameOf).join(', ')}.`}
        </Notice>
      )}

      {urteil && (view.state.phase === 'DAY_VOTE_RESOLUTION' || view.state.phase === 'DEATH_RESOLUTION') && (
        <Notice calm>
          {urteil.type === 'ELIMINATED'
            ? urteil.byMayor
              ? `Der Bürgermeister hat sich für ${nameOf(urteil.playerId)} entschieden.`
              : `Das Dorf hat ${nameOf(urteil.playerId)} herausgewählt.`
            : urteil.type === 'TIE_NO_MAYOR'
              ? 'Gleichstand ohne Bürgermeister – niemand stirbt.'
              : 'Die Abstimmung ist ausgewertet.'}
        </Notice>
      )}

      {night && (
        <>
          <p style={{ fontFamily: 'var(--serif)', fontSize: '1.15rem', margin: 0 }}>{night.title}</p>
          {night.spotlight.length > 0 ? (
            <p className="spotlight">
              Spotlight:{' '}
              <strong>{night.spotlight.map((s) => `${nameOf(s.playerId)}${s.dead ? ' (tot)' : ''}`).join(', ')}</strong>
            </p>
          ) : (
            <p className="tiny muted" style={{ margin: 0 }}>
              Diese Rolle ist nicht besetzt – die Phase wird nur inszeniert.
            </p>
          )}
        </>
      )}

      {readiness && readiness.actors.length > 0 && (
        <div>
          {readiness.actors.map((a) => (
            <div key={a.playerId} className="row tiny" style={{ justifyContent: 'space-between' }}>
              <span>{nameOf(a.playerId)}</span>
              <span className={a.status === 'CONFIRMED' ? 'tag role' : 'tag'}>
                {a.status === 'CONFIRMED' ? 'bestätigt' : a.status === 'PENDING' ? 'wählt noch' : 'offen'}
              </span>
            </div>
          ))}
        </div>
      )}

      <StepOutcome view={view} />

      {readiness?.note && <Notice calm>{readiness.note}</Notice>}
      {blocked && <Notice>Die Vampire müssen sich einig sein, bevor du weitergehen kannst.</Notice>}

      {view.nightPreview && (
        <Notice calm>
          <div>
            Stirbt heute Nacht:{' '}
            {view.nightPreview.allDeaths.length === 0 ? 'niemand' : view.nightPreview.allDeaths.map(nameOf).join(', ')}
          </div>
          {view.nightPreview.blocked.length > 0 && (
            <div className="tiny muted">
              Verhindert: {view.nightPreview.blocked.map((b) => nameOf(b.playerId)).join(', ')}
            </div>
          )}
        </Notice>
      )}

      {live.length > 0 && (
        <div className="stack">
          {live.map((a) => (
            <div key={a.id} style={{ borderTop: 'var(--edge)', paddingTop: '0.5rem' }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong>{nameOf(a.actorId)}</strong>
                <span className={a.status === 'CONFIRMED' ? 'tag role' : 'tag'}>
                  {a.status === 'CONFIRMED' ? 'bestätigt' : 'wählt noch'}
                </span>
              </div>
              <div className="row" style={{ marginTop: '0.4rem' }}>
                {a.status === 'PENDING' ? (
                  <>
                    <button
                      type="button"
                      className="tiny"
                      onClick={() => void run({ type: 'CONFIRM_ACTION', actionId: a.id }, 'Bestätigt')}
                    >
                      Für sie bestätigen
                    </button>
                    <button
                      type="button"
                      className="ghost tiny"
                      onClick={() => void run({ type: 'RESET_ACTION', actionId: a.id }, 'Freigegeben')}
                    >
                      Neu wählen lassen
                    </button>
                  </>
                ) : (
                  <ActionOverride view={view} action={a} run={run} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ProxyAction view={view} run={run} />
    </div>
  );
}

/** Welche Aktion zu welchem Nachtschritt gehört. */
const NIGHT_ACTION: Record<string, string> = {
  VAMPIRE_PHASE: 'VAMPIRE_ATTACK',
  SEER_PHASE: 'SEER_INVESTIGATE',
  WITCH_PHASE: 'WITCH_POTIONS',
  PRIEST_PHASE: 'PRIEST_PROTECT',
  DEMON_PHASE: 'DEMON_POSSESS',
  GHOST_PHASE: 'GHOST_LETTER',
};

/** Was die aufgerufene Rolle gerade entschieden hat – genau das, was der Spieler selbst sieht. */
function StepOutcome({ view }: { view: HostState }) {
  const night = view.state.night;
  const step = view.night?.step;
  if (!night || !step) return null;
  const nameOf = (id: string | null | undefined) => view.players.find((p) => p.id === id)?.name ?? '—';
  const confirmed = view.state.actions.filter((a) => a.round === view.state.round && a.status === 'CONFIRMED');
  const lines: string[] = [];

  if (step === 'VAMPIRE_PHASE') {
    const attack = confirmed.find((a) => a.kind === 'VAMPIRE_ATTACK');
    if (attack) lines.push(`Die Vampire greifen ${nameOf(attack.payload.targetId)} an.`);
  }
  if (step === 'SEER_PHASE') {
    for (const r of view.state.seerResults.filter((x) => x.night === night.number)) {
      lines.push(`${nameOf(r.seerId)} prüfte ${nameOf(r.targetId)}: ${r.result === 'EVIL' ? 'verdächtig' : 'unverdächtig'}.`);
    }
  }
  if (step === 'WITCH_PHASE') {
    const potions = confirmed.find((a) => a.kind === 'WITCH_POTIONS');
    if (potions) {
      if (potions.payload.heal) lines.push('Die Hexe setzt den Heiltrank ein.');
      if (potions.payload.poisonTargetId) lines.push(`Die Hexe vergiftet ${nameOf(potions.payload.poisonTargetId)}.`);
      if (!potions.payload.heal && !potions.payload.poisonTargetId) lines.push('Die Hexe tut nichts.');
    }
  }
  if (step === 'DEMON_PHASE') {
    const possess = confirmed.find((a) => a.kind === 'DEMON_POSSESS');
    if (possess) lines.push(`Der Dämon macht ${nameOf(possess.payload.targetId)} besessen.`);
  }
  if (step === 'GHOST_PHASE') {
    const ghost = view.state.ghost;
    if (ghost?.pending) lines.push(`Der Geist hinterlässt „${ghost.pending}“ – freigegeben wird er am Morgen.`);
  }
  if (step === 'PRIEST_PHASE') {
    const protect = confirmed.find((a) => a.kind === 'PRIEST_PROTECT');
    if (protect) lines.push(`Der Priester schützt ${nameOf(protect.payload.targetId)}.`);
  }
  if (lines.length === 0) return null;

  return (
    <div className="step-outcome">
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
  );
}

function QueuePanel({ view, run }: { view: HostState; run: Run }) {
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.name ?? id;
  const labels: Record<string, string> = {
    TRANSFER_MAYOR: 'bestimmt einen Nachfolger im Amt',
    ON_DEATH_KILL: 'reißt jemanden mit ins Jenseits',
  };
  const kinds: Record<string, 'HUNTRESS_TAKE' | 'MAYOR_SUCCESSOR'> = {
    ON_DEATH_KILL: 'HUNTRESS_TAKE',
    TRANSFER_MAYOR: 'MAYOR_SUCCESSOR',
  };
  const head = view.state.effectQueue[0];
  const running = view.state.phase === 'DEATH_RESOLUTION';
  const [targetId, setTargetId] = useState('');
  const kind = head ? kinds[head.type] : undefined;

  return (
    <Panel title="Letzte Verfügungen">
      <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
        {view.state.effectQueue.map((fx, i) => (
          <li key={fx.id}>
            {i === 0 ? <strong>{nameOf(fx.playerId)}</strong> : nameOf(fx.playerId)} {labels[fx.type] ?? fx.type}
          </li>
        ))}
      </ul>

      {!running && (
        <button
          type="button"
          className="primary"
          style={{ marginTop: '0.7rem' }}
          onClick={() => void run({ type: 'START_DEATH_RESOLUTION' }, 'Verfügungen')}
        >
          Jetzt auflösen
        </button>
      )}

      {running && head && kind && (
        <div className="stack" style={{ marginTop: '0.8rem' }}>
          <p className="tiny muted">
            {nameOf(head.playerId)} entscheidet in der eigenen Ansicht. Klemmt es, kannst du hier
            stellvertretend bestimmen – das gilt sofort.
          </p>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">Ziel wählen …</option>
            {view.players
              .filter((p) => p.status === 'ALIVE' && p.id !== head.playerId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="primary"
            disabled={!targetId}
            onClick={() => {
              void run(
                { type: 'SUBMIT_ACTION', kind, payload: { targetId }, actorId: head.playerId },
                'Verfügung',
              );
              setTargetId('');
            }}
          >
            Für {nameOf(head.playerId)} bestimmen
          </button>
        </div>
      )}
    </Panel>
  );
}

function VotePanel({ view, run }: { view: HostState; run: Run }) {
  const vote = view.dayVote;
  if (!vote) return null;
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.name ?? id;
  const done = vote.progress.confirmed.length;
  const need = vote.progress.required.length;

  return (
    <Panel title="Abstimmung" aside={<span className="tag">{`${done}/${need}`}</span>}>
      <div className="bar">
        <span style={{ width: `${need ? Math.round((done / need) * 100) : 0}%` }} />
      </div>
      {view.state.dayVote?.revealed ? (
        <ul style={{ margin: '0.7rem 0 0', paddingLeft: '1.1rem' }}>
          {vote.tally.counts.map((c) => (
            <li key={c.playerId}>
              {nameOf(c.playerId)}: {c.votes}
            </li>
          ))}
        </ul>
      ) : (
        <p className="tiny muted" style={{ marginTop: '0.7rem' }}>
          Offen: {vote.progress.missing.map(nameOf).join(', ') || 'niemand'}
        </p>
      )}
      {view.state.phase === 'MAYOR_TIEBREAK' && (
        <Notice>
          Gleichstand. Der Bürgermeister entscheidet zwischen{' '}
          {(view.state.dayVote?.tieCandidates ?? []).map(nameOf).join(' und ')}.
        </Notice>
      )}
      {view.state.dayVote?.resolution?.type === 'TIE_NO_MAYOR' && (
        <Notice calm>Gleichstand ohne Bürgermeister – niemand stirbt.</Notice>
      )}
      {view.state.dayVote?.resolution?.type === 'ELIMINATED' && (
        <Notice>
          {view.state.dayVote.resolution.byMayor
            ? `Der Bürgermeister hat sich für ${nameOf(view.state.dayVote.resolution.playerId)} entschieden.`
            : `${nameOf(view.state.dayVote.resolution.playerId)} wurde herausgewählt.`}
        </Notice>
      )}
      {view.state.phase === 'DAY_VOTE' && (
        <button
          type="button"
          className="primary"
          style={{ marginTop: '0.7rem' }}
          disabled={done < need}
          onClick={() => void run({ type: 'REVEAL_VOTES' }, 'Stimmen')}
        >
          Stimmen aufdecken
        </button>
      )}
    </Panel>
  );
}

function ElectionPanel({ view, run }: { view: HostState; run: Run }) {
  const election = view.election;
  if (!election) return null;
  const nameOf = (id: string) => view.players.find((p) => p.id === id)?.name ?? id;
  const [pick, setPick] = useState<string | null>(null);
  const open = view.state.mayorElection.status === 'OPEN';

  return (
    <Panel title="Bürgermeisterwahl" aside={<span className="tag">{`${election.totalVotes} Stimmen`}</span>}>
      <p className="tiny muted">
        Der Chat von {view.lobby.twitchChannel ? `#${view.lobby.twitchChannel}` : 'Twitch'} wählt mit{' '}
        <code>!BM {'<'}Nummer{'>'}</code>.
      </p>
      {election.entries.map((e) => (
        <div key={e.playerId} style={{ marginBottom: '0.4rem' }}>
          <div className="row tiny" style={{ justifyContent: 'space-between' }}>
            <span>
              {e.number}. {nameOf(e.playerId)}
            </span>
            <span>{e.votes}</span>
          </div>
          <div className="bar">
            <span style={{ width: `${Math.round((e.votes / Math.max(1, election.totalVotes)) * 100)}%` }} />
          </div>
        </div>
      ))}
      {!open && (
        <div className="stack" style={{ marginTop: '0.8rem' }}>
          {election.leaders.length > 1 && <Notice calm>Gleichstand – du entscheidest, wer das Amt übernimmt.</Notice>}
          <select value={pick ?? ''} onChange={(e) => setPick(e.target.value || null)}>
            <option value="">Bürgermeister wählen …</option>
            {view.players
              .filter((p) => p.status === 'ALIVE')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name}
                  {election.leaders.includes(p.id) ? ' (führt)' : ''}
                </option>
              ))}
          </select>
          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={!pick}
              onClick={() => void run({ type: 'CONFIRM_MAYOR', playerId: pick }, 'Bürgermeister')}
            >
              Amt bestätigen
            </button>
            <button
              type="button"
              disabled={!pick}
              onClick={() => void run({ type: 'CONFIRM_MAYOR', playerId: pick, override: true }, 'Bürgermeister')}
            >
              Gegen das Ergebnis setzen
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}

function RosterPanel({ view, run }: { view: HostState; run: Run }) {
  const cameraOf = (id: string) => view.cameras.find((c) => c.playerId === id);
  const onlineOf = (id: string) => view.sessions.find((s) => s.playerId === id)?.connected ?? false;

  return (
    <Panel title="Noxbury" aside={<span className="tag">{`${view.players.length} Personen`}</span>}>
      <div className="scroll">
        {view.players.map((p) => (
          <div key={p.id} className={`person${p.status === 'DEAD' ? ' dead' : ''}`}>
            <Seal number={p.number} dead={p.status === 'DEAD'} mayor={p.isMayor} />
            <span className="name">{p.name}</span>
            <span className="spacer" />
            {p.role && <span className="tag role">{roleName(p.role)}</span>}
            {p.isDrunk && <span className="tag">{p.drunkRevealed ? 'war Trunkenbold' : 'Trunkenbold'}</span>}
            {p.loverOf && <span className="tag">verliebt</span>}
            {p.possessed && <span className="tag alarm">besessen</span>}
            {p.isGhost && <span className="tag">Geist</span>}
            {p.resources.healPotion && <span className="tag">Heiltrank</span>}
            {p.resources.poisonPotion && <span className="tag">Gift</span>}
            {p.status === 'PAUSED' && <span className="tag">Pause</span>}
            {p.status === 'REMOVED' && <span className="tag alarm">entfernt</span>}
            <span className={`dot${onlineOf(p.id) ? ' live' : ' off'}`} title={onlineOf(p.id) ? 'verbunden' : 'getrennt'} />
            <CameraDot status={cameraOf(p.id)?.status ?? 'NONE'} />
            {p.status !== 'REMOVED' && (
              <button
                type="button"
                className="ghost tiny"
                onClick={() => void run({ type: 'REMOVE_PLAYER', playerId: p.id }, 'Entfernt')}
              >
                entfernen
              </button>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}


/** Notfallkorrektur einer bereits bestätigten Entscheidung. */
function ActionOverride({
  view,
  action,
  run,
}: {
  view: HostState;
  action: HostState['state']['actions'][number];
  run: Run;
}) {
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState(String(action.payload.targetId ?? ''));

  if (!open) {
    return (
      <button type="button" className="ghost tiny" onClick={() => setOpen(true)}>
        Im Notfall ändern
      </button>
    );
  }

  return (
    <div className="stack" style={{ width: '100%' }}>
      <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
        <option value="">kein Ziel</option>
        {view.players
          .filter((p) => p.status === 'ALIVE')
          .map((p) => (
            <option key={p.id} value={p.id}>
              {p.number}. {p.name}
            </option>
          ))}
      </select>
      <div className="row">
        <button
          type="button"
          className="danger tiny"
          onClick={() => {
            void run(
              {
                type: 'ADMIN',
                change: { kind: 'OVERRIDE_ACTION', actionId: action.id, payload: { targetId: targetId || null } },
                reason: 'Notfallkorrektur im Regiepult',
              },
              'Korrektur',
            );
            setOpen(false);
          }}
        >
          Übernehmen
        </button>
        <button type="button" className="ghost tiny" onClick={() => setOpen(false)}>
          Abbrechen
        </button>
      </div>
    </div>
  );
}

/** Stellvertretende Eingabe, wenn jemandes Gerät streikt. */
function ProxyAction({ view, run }: { view: HostState; run: Run }) {
  // Direkte stellvertretende Eingabe ist nur für künstlich erzeugte
  // Testfiguren erlaubt. Echte Spieler besitzen eine Session und können
  // vom Spielleiter ausschließlich über das Notfallprotokoll korrigiert werden.
  const realPlayerIds = new Set(view.sessions.map((session) => session.playerId));
  const candidates = view.players.filter(
    (p) => p.availableAction && !realPlayerIds.has(p.id),
  );
  const [actorId, setActorId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [heal, setHeal] = useState(false);
  const actor = candidates.find((p) => p.id === actorId);

  if (candidates.length === 0) return null;

  return (
    <details style={{ marginTop: '0.8rem' }}>
      <summary className="tiny muted">Für jemanden eingeben</summary>
      <div className="stack" style={{ marginTop: '0.6rem' }}>
        <select value={actorId} onChange={(e) => setActorId(e.target.value)}>
          <option value="">Wer kann gerade nicht?</option>
          {candidates.map((p) => (
            <option key={p.id} value={p.id}>
              {p.number}. {p.name}
            </option>
          ))}
        </select>
        {actor && actor.availableAction === 'WITCH_POTIONS' && (
          <>
            <label className="row">
              <input type="checkbox" style={{ width: 'auto' }} checked={heal} onChange={(e) => setHeal(e.target.checked)} />
              <span>Opfer heilen</span>
            </label>
            <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">niemanden vergiften</option>
              {view.players
                .filter((p) => p.status === 'ALIVE' && p.id !== actor.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.number}. {p.name} vergiften
                  </option>
                ))}
            </select>
            <button
              type="button"
              onClick={() =>
                void run(
                  {
                    type: 'SUBMIT_ACTION',
                    kind: 'WITCH_POTIONS',
                    payload: { heal, poisonTargetId: targetId || null },
                    actorId: actor.id,
                  },
                  'Eingabe',
                )
              }
            >
              Für {actor.name} senden
            </button>
          </>
        )}

        {actor && actor.availableAction !== 'WITCH_POTIONS' && (
          <>
            <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">Ziel wählen …</option>
              {view.players
                .filter((p) => p.status === 'ALIVE' && p.id !== actor.id)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.number}. {p.name}
                  </option>
                ))}
            </select>
            <button
              type="button"
              disabled={!targetId}
              onClick={() =>
                void run(
                  { type: 'SUBMIT_ACTION', kind: actor.availableAction, payload: { targetId }, actorId: actor.id },
                  'Eingabe',
                )
              }
            >
              Für {actor.name} senden
            </button>
          </>
        )}
      </div>
    </details>
  );
}

function WinPanel({ view, run }: { view: HostState; run: Run }) {
  const met = view.metWinConditions;
  const [chosen, setChosen] = useState<string[]>([]);

  useEffect(() => setChosen(met.map((w) => w.type)), [met.map((w) => w.type).join(',')]);

  if (view.state.phase === 'GAME_END') {
    return (
      <Panel title="Ende">
        <p>Gewonnen: {(view.state.result?.winners ?? []).map((w) => WIN_NAMES[w] ?? w).join(', ')}</p>
      </Panel>
    );
  }
  if (met.length === 0) return null;

  return (
    <Panel title="Sieg möglich">
      <Notice danger>
        <strong>Siegbedingung erreicht!</strong> Prüfe die mögliche Gewinnerseite und entscheide,
        ob du die Partie jetzt beenden möchtest.
      </Notice>
      <p className="tiny muted">Nur du siehst das. Die Partie endet erst, wenn du sie beendest.</p>
      {met.map((w) => (
        <label key={w.type} className="row">
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={chosen.includes(w.type)}
            onChange={(e) => setChosen(e.target.checked ? [...chosen, w.type] : chosen.filter((c) => c !== w.type))}
          />
          <span>
            {WIN_NAMES[w.type] ?? w.type} <span className="tiny muted">({w.detail})</span>
          </span>
        </label>
      ))}
      <button
        type="button"
        className="danger"
        style={{ marginTop: '0.7rem' }}
        disabled={chosen.length === 0}
        onClick={() => void run({ type: 'END_GAME', winners: chosen }, 'Spielende')}
      >
        Partie beenden
      </button>
    </Panel>
  );
}

/** Eigene Kamera des Spielleiters: erscheint fest in der Ecke des Overlays. */
function HostCameraPanel({
  connection,
  view,
  cameraToken,
}: {
  connection: Connection<HostState>;
  view: HostState;
  cameraToken: string;
}) {
  const videoRef =
    useRef<HTMLVideoElement | null>(null);

  const cameraSessionRef = useRef<
    Awaited<ReturnType<typeof openMediaMTXCamera>> | null
  >(null);

  const [running, setRunning] =
    useState(false);

  const [note, setNote] =
    useState<string | null>(null);

  const [previewVisible, setPreviewVisible] =
    useState(true);

  const [cameraDevices, setCameraDevices] =
    useState<CameraDevice[]>([]);

  const [selectedCameraId, setSelectedCameraId] =
    useState('');

  const browserHelp =
    getCameraBrowserHelp();

  const lobbyCode = view.lobby.code;
  const streamPath =
    hostCameraPath(lobbyCode);
  const streamUrl =
    hostCameraUrl(lobbyCode);

  async function refreshCameraDevices(
    preferredDeviceId?: string,
  ) {
    try {
      const devices =
        await getVideoDevices();

      setCameraDevices(devices);

      const preferred =
        preferredDeviceId ||
        selectedCameraId;

      if (
        preferred &&
        devices.some(
          (device) =>
            device.deviceId === preferred,
        )
      ) {
        setSelectedCameraId(preferred);
      } else if (devices.length > 0) {
        setSelectedCameraId(
          devices[0].deviceId,
        );
      } else {
        setSelectedCameraId('');
      }
    } catch (error) {
      console.error(
        '[Host-Kameraliste]',
        error,
      );
    }
  }

  useEffect(() => {
    void refreshCameraDevices();
  }, []);

  async function startCamera() {
    if (!lobbyCode) {
      setNote(
        'Lobbydaten sind noch nicht verfügbar.',
      );
      return;
    }

    try {
      setNote(
        'Kamera wird gestartet …',
      );

      cameraSessionRef.current?.stop();

      const session =
        await openMediaMTXCamera({
          streamPath,
          token: cameraToken,
          videoDeviceId:
            selectedCameraId ||
            undefined,
        });

      cameraSessionRef.current =
        session;

      const activeVideoTrack =
        session.stream.getVideoTracks()[0];

      const activeDeviceId =
        activeVideoTrack
          ?.getSettings()
          .deviceId;

      await refreshCameraDevices(
        activeDeviceId,
      );

      if (videoRef.current) {
        videoRef.current.srcObject =
          session.stream;

        await videoRef.current.play();
      }

      const result =
        await connection.emit(
          'camera:set',
          streamUrl,
        );

      if (!result.ok) {
        session.stop();
        cameraSessionRef.current =
          null;

        if (videoRef.current) {
          videoRef.current.srcObject =
            null;
        }

        setRunning(false);

        setNote(
          result.message ??
            'Die Kamera konnte nicht beim Server registriert werden.',
        );

        return;
      }

      setRunning(true);
      setPreviewVisible(true);

      setNote(
        'Kamera ist aktiv und im Overlay sichtbar.',
      );
    } catch (error) {
      console.error(
        '[MediaMTX Host-Kamera]',
        error,
      );

      cameraSessionRef.current?.stop();
      cameraSessionRef.current =
        null;

      if (videoRef.current) {
        videoRef.current.srcObject =
          null;
      }

      setRunning(false);

      if (
        error instanceof DOMException &&
        (
          error.name === 'NotAllowedError' ||
          error.name === 'SecurityError'
        )
      ) {
        setNote(
          'Der Kamerazugriff wurde nicht erlaubt. Nutze die Anleitung unten, um die Berechtigung wieder zu aktivieren.',
        );
      } else if (
        error instanceof DOMException &&
        error.name === 'NotFoundError'
      ) {
        setNote(
          'Es wurde keine verfügbare Kamera gefunden.',
        );
      } else if (
        error instanceof DOMException &&
        (
          error.name === 'NotReadableError' ||
          error.name === 'AbortError'
        )
      ) {
        setNote(
          'Die Kamera konnte nicht geöffnet werden. Möglicherweise wird sie bereits von einem anderen Programm verwendet.',
        );
      } else {
        setNote(
          error instanceof Error
            ? error.message
            : 'Die Kamera konnte nicht gestartet werden.',
        );
      }
    }
  }

  function stopCamera() {
    cameraSessionRef.current?.stop();
    cameraSessionRef.current =
      null;

    if (videoRef.current) {
      videoRef.current.srcObject =
        null;
    }

    setRunning(false);

    void connection.emit(
      'camera:set',
      null,
    );

    setNote(
      'Kamera wurde beendet.',
    );
  }

  useEffect(() => {
    return () => {
      cameraSessionRef.current?.stop();
      cameraSessionRef.current =
        null;
    };
  }, []);

  return (
    <Panel
      title="Deine Kamera"
      aside={
        <CameraDot
          status={
            running
              ? 'LIVE'
              : (
                  view.hostCamera?.status ??
                  'NONE'
                )
          }
        />
      }
    >
      <div className="stack">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          style={{
            width: '100%',
            maxWidth: '480px',
            aspectRatio: '16 / 9',
            objectFit: 'cover',
            borderRadius: '12px',
            background: '#05070b',
            display:
              running &&
              previewVisible
                ? 'block'
                : 'none',
          }}
        />

        <div className="field">
          <label htmlFor="hostname">
            Dein Name auf der Banderole
          </label>

          <input
            id="hostname"
            value={
              view.hostName ??
              'Spielleiter'
            }
            maxLength={24}
            onChange={(event) =>
              void connection.emit(
                'host:name',
                event.target.value,
              )
            }
          />
        </div>

        {!running && (
          <>
            <div className="field">
              <label htmlFor="host-camera-device">
                Kamera auswählen
              </label>

              <select
                id="host-camera-device"
                value={selectedCameraId}
                onChange={(event) =>
                  setSelectedCameraId(
                    event.target.value,
                  )
                }
              >
                {cameraDevices.length === 0 ? (
                  <option value="">
                    Kamera wird beim Start erkannt
                  </option>
                ) : (
                  cameraDevices.map(
                    (device) => (
                      <option
                        key={device.deviceId}
                        value={device.deviceId}
                      >
                        {device.label}
                      </option>
                    ),
                  )
                )}
              </select>
            </div>

            <div
              style={{
                padding: '12px 14px',
                borderRadius: '12px',
                background:
                  'rgba(255,255,255,0.05)',
              }}
            >
              <p
                className="tiny"
                style={{
                  marginTop: 0,
                  fontWeight: 700,
                }}
              >
                📷 Kamera-Hilfe für {browserHelp.browserName}
              </p>

              <p className="tiny muted">
                {browserHelp.allowText}
              </p>

              <p
                className="tiny muted"
                style={{
                  marginBottom: 0,
                }}
              >
                {browserHelp.rejectedText}
              </p>
            </div>
          </>
        )}

        <div className="row">
          {!running ? (
            <button
              type="button"
              className="primary"
              onClick={() =>
                void startCamera()
              }
            >
              Kamera starten
            </button>
          ) : (
            <button
              type="button"
              className="ghost"
              onClick={stopCamera}
            >
              Kamera stoppen
            </button>
          )}

          {running && (
            <button
              type="button"
              onClick={() =>
                setPreviewVisible(
                  (value) => !value,
                )
              }
            >
              {previewVisible
                ? 'Vorschau ausblenden'
                : 'Vorschau anzeigen'}
            </button>
          )}
        </div>

        {note && (
          <p className="tiny muted">
            {note}
          </p>
        )}

        <p className="tiny muted">
          Nur dein Kamerabild wird übertragen.
          Das Mikrofon wird nicht verwendet.
        </p>

        {running && !previewVisible && (
          <p className="tiny muted">
            Deine Vorschau ist ausgeblendet.
            Die Kamera bleibt für das Overlay
            weiterhin aktiv.
          </p>
        )}
      </div>
    </Panel>
  );
}

/** Twitch-Chats der Spieler und zusätzliche Chats des Spielleiters verwalten. */
function TwitchPanel({ connection, view }: { connection: Connection<HostState>; view: HostState }) {
  const channels = view.lobby.twitchChannels ?? [];
  const manualChannels = view.lobby.manualTwitchChannels ?? [];
  const playerChannels = view.lobby.playerTwitchChannels ?? {};

  const players = view.players.filter((p) => p.status !== 'REMOVED');

  const [value, setValue] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, string> = {};

    for (const player of players) {
      next[player.id] = playerChannels[player.id] ?? '';
    }

    setEdits(next);
  }, [view.lobby.playerTwitchChannels, view.players]);

  async function savePlayer(playerId: string) {
    const res = await connection.emit(
      'host:player-twitch:set',
      {
        playerId,
        channel: edits[playerId]?.trim() ?? '',
      },
    );

    setNote(
      res.ok
        ? 'Twitch-Zuordnung gespeichert.'
        : (res.message ?? 'Das ging nicht.'),
    );

    if (res.ok) {
      setEditingPlayerId(null);
    }
  }

  async function removePlayerChannel(playerId: string) {
    const res = await connection.emit(
      'host:player-twitch:set',
      {
        playerId,
        channel: '',
      },
    );

    setNote(
      res.ok
        ? 'Twitch-Zuordnung entfernt.'
        : (res.message ?? 'Das ging nicht.'),
    );

    if (res.ok) {
      setEditingPlayerId(null);
    }
  }

  async function addManual() {
    const res = await connection.emit('twitch:add', value);

    setNote(
      res.ok
        ? null
        : (res.message ?? 'Das ging nicht.'),
    );

    if (res.ok) {
      setValue('');
    }
  }

  async function removeManual(channel: string) {
    const res = await connection.emit('twitch:remove', channel);

    setNote(
      res.ok
        ? null
        : (res.message ?? 'Das ging nicht.'),
    );
  }

  return (
    <Panel
      title="Twitch-Chats"
      aside={<span className="tag">{channels.length}</span>}
    >
      <p className="tiny muted">
        Aus allen verbundenen Chats zählen Stimmen mit <code>!BM</code>.
      </p>

      <h3>Spieler-Chats</h3>

      <div className="stack">
        {players.map((player) => {
          const saved = playerChannels[player.id] ?? '';
          const editing = editingPlayerId === player.id;

          return (
            <div
              key={player.id}
              className="person"
              style={{
                padding: '0.45rem 0',
                gap: '0.6rem',
                flexWrap: 'wrap',
              }}
            >
              {!editing ? (
                <>
                  <span
                    className="name"
                    style={{ minWidth: '8rem' }}
                  >
                    {player.name}
                  </span>

                  <span
                    className="tiny muted"
                    style={{
                      flex: 1,
                      minWidth: '10rem',
                    }}
                  >
                    {saved ? `#${saved}` : 'Kein Twitch-Kanal'}
                  </span>

                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => {
                      setEdits((current) => ({
                        ...current,
                        [player.id]: saved,
                      }));
                      setEditingPlayerId(player.id);
                      setNote(null);
                    }}
                  >
                    Bearbeiten
                  </button>
                </>
              ) : (
                <>
                  <span
                    className="name"
                    style={{ minWidth: '8rem' }}
                  >
                    {player.name}
                  </span>

                  <input
                    autoFocus
                    value={edits[player.id] ?? ''}
                    placeholder="Twitch-Name"
                    style={{
                      flex: 1,
                      minWidth: '10rem',
                    }}
                    onChange={(event) =>
                      setEdits((current) => ({
                        ...current,
                        [player.id]: event.target.value,
                      }))
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        void savePlayer(player.id);
                      }

                      if (event.key === 'Escape') {
                        setEdits((current) => ({
                          ...current,
                          [player.id]: saved,
                        }));
                        setEditingPlayerId(null);
                      }
                    }}
                  />

                  <button
                    type="button"
                    className="tiny"
                    onClick={() => void savePlayer(player.id)}
                  >
                    Speichern
                  </button>

                  {saved && (
                    <button
                      type="button"
                      className="ghost tiny"
                      onClick={() => void removePlayerChannel(player.id)}
                    >
                      Entfernen
                    </button>
                  )}

                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => {
                      setEdits((current) => ({
                        ...current,
                        [player.id]: saved,
                      }));
                      setEditingPlayerId(null);
                      setNote(null);
                    }}
                  >
                    Abbrechen
                  </button>
                </>
              )}
            </div>
          );
        })}

        {players.length === 0 && (
          <p className="tiny muted">
            Noch keine Spieler in der Lobby.
          </p>
        )}
      </div>

      <h3 style={{ marginTop: '1.2rem' }}>
        Zusätzliche Chats
      </h3>

      <p className="tiny muted">
        Hier kannst du weitere Twitch-Chats unabhängig von einem Spieler hinzufügen.
      </p>

      <div>
        {manualChannels.map((channel) => (
          <div
            key={channel}
            className="person"
            style={{ padding: '0.3rem 0' }}
          >
            <span className="name">#{channel}</span>
            <span className="spacer" />

            <button
              type="button"
              className="ghost tiny"
              onClick={() => void removeManual(channel)}
            >
              entfernen
            </button>
          </div>
        ))}

        {manualChannels.length === 0 && (
          <p className="tiny muted">
            Keine zusätzlichen Chats eingetragen.
          </p>
        )}
      </div>

      <div
        className="row"
        style={{ marginTop: '0.6rem' }}
      >
        <input
          value={value}
          placeholder="Kanalname oder twitch.tv/…"
          style={{ flex: 1, minWidth: '10rem' }}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              void addManual();
            }
          }}
        />

        <button
          type="button"
          disabled={!value.trim()}
          onClick={() => void addManual()}
        >
          Hinzufügen
        </button>
      </div>

      {note && <p className="tiny">{note}</p>}
    </Panel>
  );
}

function LinksPanel({ view }: { view: HostState }) {
  const base = window.location.origin;
  const join = `${base}/game/${view.lobby.code}`;
  const overlay = `${base}/game/${view.lobby.code}/overlay?key=${view.lobby.overlayKey}`;
  const copy = (text: string) => void navigator.clipboard?.writeText(text);

  return (
    <Panel title="Links">
      <div className="field">
        <label htmlFor="join">Für die Spieler</label>
        <input id="join" readOnly value={join} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="tiny" style={{ marginTop: '0.3rem' }} onClick={() => copy(join)}>
          Kopieren
        </button>
      </div>
      <div className="field">
        <label htmlFor="obs">Für OBS (bleibt immer gleich)</label>
        <input id="obs" readOnly value={overlay} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="tiny" style={{ marginTop: '0.3rem' }} onClick={() => copy(overlay)}>
          Kopieren
        </button>
      </div>
    </Panel>
  );
}

function EmergencyPlayerAction({
  view,
  playerId,
  run,
}: {
  view: HostState;
  playerId: string;
  run: Run;
}) {
  const [targetId, setTargetId] = useState('');
  const [heal, setHeal] = useState(false);

  const player = view.players.find((p) => p.id === playerId);
  const realPlayerIds = new Set(view.sessions.map((session) => session.playerId));

  if (!player || !realPlayerIds.has(player.id) || !player.availableAction) return null;

  const kind = player.availableAction;

  return (
    <div
      className="stack"
      style={{
        borderTop: 'var(--edge)',
        paddingTop: '0.8rem',
        marginTop: '0.4rem',
      }}
    >
      <strong>Aktuelle Entscheidung stellvertretend eingeben</strong>
      <p className="tiny muted" style={{ margin: 0 }}>
        Nur für den Notfall, wenn {player.name} die aktuelle Rollenentscheidung nicht selbst senden kann.
      </p>

      {kind === 'WITCH_POTIONS' ? (
        <>
          <label className="row">
            <input
              type="checkbox"
              style={{ width: 'auto' }}
              checked={heal}
              onChange={(e) => setHeal(e.target.checked)}
            />
            <span>Heiltrank verwenden</span>
          </label>

          <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">niemanden vergiften</option>
            {view.players
              .filter((p) => p.status === 'ALIVE' && p.id !== player.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name} vergiften
                </option>
              ))}
          </select>

          <button
            type="button"
            className="danger"
            onClick={() =>
              void run(
                {
                  type: 'SUBMIT_ACTION',
                  kind: 'WITCH_POTIONS',
                  payload: {
                    heal,
                    poisonTargetId: targetId || null,
                  },
                  actorId: player.id,
                },
                `Notfalleingabe für ${player.name}`,
              )
            }
          >
            Entscheidung für {player.name} senden
          </button>
        </>
      ) : (
        <>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            <option value="">Ziel wählen …</option>
            {view.players
              .filter((p) => p.status === 'ALIVE' && p.id !== player.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number}. {p.name}
                </option>
              ))}
          </select>

          <button
            type="button"
            className="danger"
            disabled={!targetId}
            onClick={() =>
              void run(
                {
                  type: 'SUBMIT_ACTION',
                  kind,
                  payload: { targetId },
                  actorId: player.id,
                },
                `Notfalleingabe für ${player.name}`,
              )
            }
          >
            Entscheidung für {player.name} senden
          </button>
        </>
      )}
    </div>
  );
}

function AdminPanel({ view, run }: { view: HostState; run: Run }) {
  const [open, setOpen] = useState(false);
  const [playerId, setPlayerId] = useState('');
  const [role, setRole] = useState<RoleId>('VILLAGER');
  const [reason, setReason] = useState('');
  const player = view.players.find((p) => p.id === playerId);

  return (
    <Panel
      title="Notfallkorrekturen"
      aside={
        <button type="button" className="ghost tiny" onClick={() => setOpen(!open)}>
          {open ? 'Schließen' : 'Öffnen'}
        </button>
      }
    >
      {!open ? (
        <p className="tiny muted">Rollen, Leben, Amt und Tränke ändern. Jede Korrektur wird protokolliert.</p>
      ) : (
        <div className="stack">
          <select value={playerId} onChange={(e) => setPlayerId(e.target.value)}>
            <option value="">Person wählen …</option>
            {view.players.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number}. {p.name}
              </option>
            ))}
          </select>
          <input placeholder="Grund (erscheint im Protokoll)" value={reason} onChange={(e) => setReason(e.target.value)} />
          {player && (
            <>
              <div className="row">
                <select value={role} onChange={(e) => setRole(e.target.value as RoleId)}>
                  {ROLE_IDS.map((r) => (
                    <option key={r} value={r}>
                      {roleName(r)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => void run({ type: 'ADMIN', change: { kind: 'SET_ROLE', playerId, role }, reason }, 'Rolle')}
                >
                  Rolle setzen
                </button>
              </div>
              <div className="row">
                <button
                  type="button"
                  onClick={() =>
                    void run(
                      {
                        type: 'ADMIN',
                        change: { kind: 'SET_LIFE', playerId, life: player.status === 'DEAD' ? 'ALIVE' : 'DEAD', applyEffects: true },
                        reason,
                      },
                      'Leben',
                    )
                  }
                >
                  {player.status === 'DEAD' ? 'Wiederbeleben' : 'Für tot erklären'}
                </button>
                <button
                  type="button"
                  onClick={() =>
                    void run(
                      {
                        type: 'ADMIN',
                        change: { kind: 'SET_PRESENCE', playerId, presence: player.status === 'PAUSED' ? 'ACTIVE' : 'PAUSED' },
                        reason,
                      },
                      'Status',
                    )
                  }
                >
                  {player.status === 'PAUSED' ? 'Zurückholen' : 'Pausieren'}
                </button>
                <button
                  type="button"
                  onClick={() => void run({ type: 'ADMIN', change: { kind: 'SET_MAYOR', playerId }, reason }, 'Amt')}
                >
                  Zum Bürgermeister
                </button>
              </div>
              <EmergencyPlayerAction view={view} playerId={playerId} run={run} />

              {player.role === 'WITCH' && (
                <div className="row">
                  <button
                    type="button"
                    onClick={() =>
                      void run(
                        {
                          type: 'ADMIN',
                          change: { kind: 'SET_POTIONS', playerId, heal: true, poison: true },
                          reason,
                        },
                        'Tränke',
                      )
                    }
                  >
                    Beide Tränke zurückgeben
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}

function LogPanel({ connection }: { connection: Connection<HostState> }) {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const view = connection.view;
  const seq = view?.state.counters.seq ?? 0;

  useEffect(() => {
    let alive = true;
    void connection.emit('log:read', 300).then((res) => {
      if (alive && res.ok && Array.isArray(res.data)) setEntries((res.data as LogEntry[]).slice().reverse());
    });
    return () => {
      alive = false;
    };
  }, [seq]);

  const nameOf = (id: unknown) =>
    view?.players.find((p) => p.id === id)?.name ?? (typeof id === 'string' ? '—' : '—');

  const lines = entries
    .map((e) => ({ entry: e, text: describeEvent(e, nameOf) }))
    .filter((x): x is { entry: LogEntry; text: string } => !!x.text);

  return (
    <Panel title="Protokoll" aside={<span className="tag">{lines.length}</span>}>
      <div className="log">
        <ul>
          {lines.map(({ entry, text }) => (
            <li key={entry.seq}>
              <span className="muted">{timeOfDay(entry.at)}</span> {text}
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
